import { useMemo } from 'react';
import Empty from 'constants/empty';
import LanguageLocale from 'utils/LanguageLocale';
import DeliveryFrequencyUtils from 'utils/DeliveryFrequency';
import DateUtils from 'utils/Date';
import StringUtils from 'utils/String';
import { formatFlexIncentivesDiscount, formatPrice } from 'components/RichProfile/MyAccount/AutoReplenishment/utils/price';
import { getFeatureFlags } from 'components/RichProfile/MyAccount/AutoReplenishment/utils/featureFlags';
import SubscriptionItemEN from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/locales/SubscriptionItem_en_US';
import SubscriptionItemFR from 'components/RichProfile/MyAccount/AutoReplenishment/SubscriptionItem/locales/SubscriptionItem_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocale;
const { formatFrequencyType, formatSavingAmountString } = DeliveryFrequencyUtils;

const getText = getLocalisedDataFromResource(SubscriptionItemEN, SubscriptionItemFR);

const STATUS = {
    active: 'ACTIVE',
    paused: 'PAUSED',
    canceled: 'UNSUBSCRIBED'
};

/**
 * Custom hook to transform subscription data for display in SubscriptionItem component.
 * Handles localization, product pricing, promotional data, and delivery scheduling.
 *
 * @param {Object} params - Hook parameters
 * @returns {Object} Transformed subscription data including product, localization, and promotional details
 */
export function useSubscriptionItem({ subscription }) {
    const {
        frequency, status, nextScheduleRunDate, frequencyType, items = Empty.Array
    } = subscription || Empty.Object;
    const product = items?.[0] || Empty.Object;

    const localization = useMemo(() => {
        return {
            annuallyWithSubscription: getText('annuallyWithSubscription'),
            autoReplenishPromoInfoModalButton: getText('autoReplenishPromoInfoModalButton'),
            autoReplenishPromoInfoModalMsg1: getText('autoReplenishPromoInfoModalMsg1'),
            autoReplenishPromoInfoModalMsg2: getText('autoReplenishPromoInfoModalMsg2'),
            autoReplenishPromoInfoModalTitle: getText('autoReplenishPromoInfoModalTitle'),
            before: getText('before'),
            deliverEvery: getText('deliverEvery'),
            deliveriesLeft: getText('deliveriesLeft', ['{0}']),
            discountValidUntil: getText('discountValidUntil', ['{0}']),
            discountsValidUntil: getText('discountsValidUntil', ['{0}']),
            getItBefore: getText('getItBefore'),
            getItNow: getText('getItNow'),
            itemText: getText('item', ['{0}']),
            lastDeliveryLeft: getText('lastDeliveryLeft', ['{0}']),
            manageSubscription: getText('manageSubscription'),
            nextShipment: getText('nextShipment'),
            or: getText('or'),
            paused: getText('paused'),
            pausedDelivery: getText('pausedDelivery'),
            resumeSubscription: getText('resumeSubscription'),
            save: getText('save'),
            savePercentage: getText('savePercentage', ['{0}']),
            stillSave: getText('stillSave'),
            unsubscribe: getText('unsubscribe')
        };
    }, []);

    const featureFlags = useMemo(getFeatureFlags, []);

    const productData = useMemo(() => {
        const hasFlexIncentive = !!(featureFlags.isFlexIncentivesEnabled && product.flexDiscountedPrice);

        return {
            ...product,
            productPrice: formatPrice(product),
            savingAmount: hasFlexIncentive ? formatFlexIncentivesDiscount(product) : formatSavingAmountString(product, product.qty)
        };
    }, [product, featureFlags.isFlexIncentivesEnabled]);

    const hotDealData = useMemo(() => {
        if (product.acceleratedPromotion) {
            const { promoExpirationDate, remainingOrderCount, discountAmount, acceleratedDiscountReclaimPossible } = product.acceleratedPromotion;

            return {
                acceleratedDiscountReclaimPossible,
                discountDeliveriesLeft: StringUtils.format(
                    remainingOrderCount > 1 ? localization.deliveriesLeft : localization.lastDeliveryLeft,
                    Math.ceil(discountAmount)
                ),
                discountsValidUntilMessage: StringUtils.format(
                    remainingOrderCount > 1 ? localization.discountsValidUntil : localization.discountValidUntil,
                    DateUtils.formatDateMDY(promoExpirationDate, true, false, true)
                ),
                formattedPromoExpiryDate: DateUtils.getDateInMMDD(promoExpirationDate),
                isPromoExpirationSoon: DateUtils.getMonthDiff(new Date(), new Date(promoExpirationDate)) < 1,
                remainingOrderCount,
                savePercentageText: StringUtils.format(localization.savePercentage, Math.ceil(discountAmount)),
                transformedPromoExpiryDate: DateUtils.getDateInMMDDYYYYShortMonth(promoExpirationDate)
            };
        }

        return Empty.Object;
    }, [
        localization.deliveriesLeft,
        localization.discountValidUntil,
        localization.discountsValidUntil,
        localization.lastDeliveryLeft,
        localization.savePercentage,
        product.acceleratedPromotion
    ]);

    const subscriptionData = useMemo(() => {
        const isActive = status === STATUS.active;
        const isPaused = status === STATUS.paused;

        return {
            isPaused,
            isActive,
            frequencyDelivery: formatFrequencyType(frequency, frequencyType),
            promiseDate: isPaused || DateUtils.getPromiseDate(`${nextScheduleRunDate}T00:00`)
        };
    }, [frequency, frequencyType, status, nextScheduleRunDate]);

    const shouldShowItemVariation = !!(product?.variationType && product?.variationValue && product?.skuId);

    return {
        featureFlags,
        hotDealData,
        localization,
        product: productData,
        shouldShowItemVariation,
        subscriptionData
    };
}
